/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Table, TBody, TD, TH, THead, TR } from '@atlaskit/table/primitives';

import HorizontalScrollContainer from '../horizontal-scroll-container';
import CollapsingTopMargin from '../utils/collapsing-top-margin';

const fontWeightMap = {
  '400': 'Regular',
  '500': 'Medium',
  '700': 'Bold',
};

type TypographyValue = {
  cleanName: string;
  fontSize: string;
  lineHeight: string;
  letterSpacing: number;
  fontWeight: number;
};

type TypographyTableProps = {
  previewText: string;
  values: TypographyValue[];
  isMonospace?: boolean;
  testId?: string;
};

const monospaceStyles = css({
  fontFamily: 'ui-monospace, Menlo, "Segoe UI Mono", "Ubuntu Mono", monospace',
});

/**
 * __Typography table__
 */
export const TypographyTable = ({
  previewText,
  values,
  isMonospace,
  testId,
}: TypographyTableProps) => {
  return (
    <CollapsingTopMargin>
      <HorizontalScrollContainer>
        <Table testId={testId}>
          <THead>
            <TR isBodyRow={false}>
              <TH width="560px">System Font Preview</TH>
              <TH width="240px">font-weight</TH>
              <TH width="240px">font-size (px/rem)</TH>
              <TH width="240px">line-height (px/rem)</TH>
              <TH width="240px">letter-spacing (em)</TH>
            </TR>
          </THead>
          <TBody>
            {values.map(token => {
              const { cleanName, ...styles } = token;
              const fontSizeValue = token.fontSize.slice(0, -2);
              const fontSizeString = `${fontSizeValue} / ${
                Number(fontSizeValue) / 16
              }`;

              const lineHeightValue = token.lineHeight.slice(0, -2);
              const lineHeightString = `${lineHeightValue} / ${
                Number(lineHeightValue) / 16
              }`;

              const fontWeightValue =
                token.fontWeight.toString() as keyof typeof fontWeightMap;

              const textStyles = isMonospace ? monospaceStyles : {};

              // All the `<p>` tags will be replaced with Text components so not too concerned about css usage
              return (
                <TR key={token.cleanName}>
                  <TD>
                    <p css={css([{ ...styles, width: '10ch' }, textStyles])}>
                      {previewText}
                    </p>
                  </TD>
                  <TD>
                    <p css={monospaceStyles}>
                      {token.fontWeight} / {fontWeightMap[fontWeightValue]}
                    </p>
                  </TD>
                  <TD>
                    <p css={monospaceStyles}>{fontSizeString}</p>
                  </TD>
                  <TD>
                    <p css={monospaceStyles}>{lineHeightString}</p>
                  </TD>
                  <TD>
                    <p css={monospaceStyles}>{token.letterSpacing}</p>
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      </HorizontalScrollContainer>
    </CollapsingTopMargin>
  );
};
